import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  CheckCircleOutline,
  CloudUpload,
  ErrorOutline,
  WarningAmber,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { usePhoneBulkUpdateMutation } from "../../apis/appApi"

// --- CSV parsing (reused from ClientImportDialog pattern) ---

function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

function detectDelimiter(firstLine: string): string {
  const commaCount = (firstLine.match(/,/g) || []).length
  const semicolonCount = (firstLine.match(/;/g) || []).length
  return semicolonCount > commaCount ? ";" : ","
}

function parseCSVLine(line: string, delimiter: string): string[] {
  const fields: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === delimiter) {
        fields.push(current.trim())
        current = ""
      } else {
        current += ch
      }
    }
  }
  fields.push(current.trim())
  return fields
}

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const cleaned = stripBom(text)
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return { headers: [], rows: [] }

  const delimiter = detectDelimiter(lines[0])
  const headers = parseCSVLine(lines[0], delimiter)
  const rows = lines.slice(1).map((line) => parseCSVLine(line, delimiter))
  return { headers, rows }
}

// --- Types ---

type PhoneBulkRow = {
  clientId: number
  phone: string
  label?: "HOME" | "WORK" | "MOBILE" | "OTHER"
}

type MappedRow = {
  data: PhoneBulkRow
  valid: boolean
  errors: string[]
}

type BulkResult = {
  created: number
  updated: number
  failed: number
  errors: { row: number; message: string }[]
}

type Props = {
  open: boolean
  onClose: () => void
  onSuccess: (message: string) => void
}

const VALID_LABELS = ["HOME", "WORK", "MOBILE", "OTHER"]

export const PhoneBulkUpdateDialog: FC<Props> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [activeStep, setActiveStep] = useState(0)
  const [fileName, setFileName] = useState("")
  const [csvRows, setCsvRows] = useState<string[][]>([])
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const [phoneBulkUpdate, { isLoading: isUpdating }] =
    usePhoneBulkUpdateMutation()

  const steps = [
    t("clients.phoneBulk.step.upload"),
    t("clients.phoneBulk.step.preview"),
    t("clients.phoneBulk.step.results"),
  ]

  // --- Auto-detect column indices ---
  const columnIndices = useMemo(() => {
    const lower = csvHeaders.map((h) => h.toLowerCase().trim())
    return {
      clientId: lower.findIndex(
        (h) =>
          h === "clientid" ||
          h === "client_id" ||
          h === "client id" ||
          h === "id",
      ),
      phone: lower.findIndex(
        (h) =>
          h === "phone" ||
          h === "telephone" ||
          h === "tel" ||
          h === "phone_number" ||
          h === "phone number" ||
          h === "numero" ||
          h === "numéro",
      ),
      label: lower.findIndex(
        (h) => h === "label" || h === "type" || h === "etiquette" || h === "étiquette",
      ),
    }
  }, [csvHeaders])

  const hasRequiredColumns = columnIndices.clientId >= 0 && columnIndices.phone >= 0

  // --- Map and validate rows ---
  const mappedRows = useMemo((): MappedRow[] => {
    if (!hasRequiredColumns) return []

    return csvRows.map((row) => {
      const errors: string[] = []
      const clientIdRaw = row[columnIndices.clientId]?.trim()
      const phone = row[columnIndices.phone]?.trim() ?? ""
      const labelRaw =
        columnIndices.label >= 0
          ? row[columnIndices.label]?.trim().toUpperCase()
          : undefined

      const clientId = parseInt(clientIdRaw, 10)
      if (isNaN(clientId) || clientId <= 0) {
        errors.push(t("clients.phoneBulk.preview.errorClientId"))
      }

      if (!phone) {
        errors.push(t("clients.phoneBulk.preview.errorPhone"))
      }

      let label: "HOME" | "WORK" | "MOBILE" | "OTHER" | undefined
      if (labelRaw && VALID_LABELS.includes(labelRaw)) {
        label = labelRaw as typeof label
      }

      return {
        data: { clientId: clientId || 0, phone, label },
        valid: errors.length === 0,
        errors,
      }
    })
  }, [csvRows, columnIndices, hasRequiredColumns, t])

  const validRows = useMemo(
    () => mappedRows.filter((r) => r.valid),
    [mappedRows],
  )
  const invalidRows = useMemo(
    () => mappedRows.filter((r) => !r.valid),
    [mappedRows],
  )

  // --- File handling ---
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (evt) => {
        const text = evt.target?.result as string
        const { headers, rows } = parseCSV(text)
        setCsvHeaders(headers)
        setCsvRows(rows)
        setFileName(file.name)
      }
      reader.readAsText(file, "utf-8")
      e.target.value = ""
    },
    [],
  )

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    try {
      const result = await phoneBulkUpdate({
        rows: validRows.map((r) => r.data),
      }).unwrap()
      setBulkResult(result)
      setActiveStep(2)
      if (result.failed === 0) {
        onSuccess(t("clients.phoneBulk.results.success"))
      }
    } catch {
      setBulkResult({
        created: 0,
        updated: 0,
        failed: validRows.length,
        errors: [{ row: 0, message: "Request failed" }],
      })
      setActiveStep(2)
    }
  }, [phoneBulkUpdate, validRows, onSuccess, t])

  // --- Navigation ---
  const handleNext = useCallback(() => {
    if (activeStep === 1) {
      handleSubmit()
    } else {
      setActiveStep((s) => s + 1)
    }
  }, [activeStep, handleSubmit])

  const handleBack = useCallback(() => {
    setActiveStep((s) => s - 1)
  }, [])

  const handleClose = useCallback(() => {
    setActiveStep(0)
    setFileName("")
    setCsvHeaders([])
    setCsvRows([])
    setBulkResult(null)
    setShowErrors(false)
    onClose()
  }, [onClose])

  const isNextDisabled = (() => {
    if (activeStep === 0) return !hasRequiredColumns || csvRows.length === 0
    if (activeStep === 1) return validRows.length === 0 || isUpdating
    return false
  })()

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-101") }}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="phone-bulk-update-dialog"
    >
      <DialogTitle>{t("clients.phoneBulk.title")}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Upload */}
        {activeStep === 0 && (
          <Box {...uiSurface("UI-DLV2-102")} sx={{ textAlign: "center", py: 4 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "grey.300",
                borderRadius: 2,
                p: 4,
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {t("clients.phoneBulk.upload.dropzone")}
              </Typography>
              <Button variant="outlined" size="small" sx={{ mt: 2 }}>
                {t("clients.phoneBulk.upload.browse")}
              </Button>
            </Box>
            <Box sx={{ mt: 2 }}>
              {fileName ? (
                <Typography variant="body2" color="text.primary">
                  {t("clients.phoneBulk.upload.fileSelected", {
                    name: fileName,
                    count: csvRows.length,
                  })}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("clients.phoneBulk.upload.noFile")}
                </Typography>
              )}
            </Box>
            {!hasRequiredColumns && csvHeaders.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t("clients.phoneBulk.upload.missingColumns")}
              </Alert>
            )}
            <Alert severity="info" sx={{ mt: 2, textAlign: "left" }}>
              {t("clients.phoneBulk.upload.format")}
            </Alert>
          </Box>
        )}

        {/* Step 2: Preview */}
        {activeStep === 1 && (
          <Box {...uiSurface("UI-DLV2-103")}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip
                label={t("clients.phoneBulk.preview.total", {
                  count: mappedRows.length,
                })}
                variant="outlined"
              />
              <Chip
                icon={<CheckCircleOutline />}
                label={t("clients.phoneBulk.preview.valid", {
                  count: validRows.length,
                })}
                color="success"
                variant="outlined"
              />
              {invalidRows.length > 0 && (
                <Chip
                  icon={<ErrorOutline />}
                  label={t("clients.phoneBulk.preview.invalid", {
                    count: invalidRows.length,
                  })}
                  color="error"
                  variant="outlined"
                />
              )}
            </Stack>

            {isUpdating && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("clients.phoneBulk.updating")}
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.phoneBulk.column.clientId")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.phoneBulk.column.phone")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.phoneBulk.column.label")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappedRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor: row.valid ? undefined : "error.50",
                        "&:hover": {
                          bgcolor: row.valid ? undefined : "error.100",
                        },
                      }}
                    >
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.data.clientId || "-"}</TableCell>
                      <TableCell>{row.data.phone || "-"}</TableCell>
                      <TableCell>{row.data.label || "-"}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <CheckCircleOutline
                            fontSize="small"
                            color="success"
                          />
                        ) : (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <WarningAmber fontSize="small" color="error" />
                            <Typography variant="caption" color="error">
                              {row.errors[0]}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 3: Results */}
        {activeStep === 2 && bulkResult && (
          <Box {...uiSurface("UI-DLV2-104")} sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("clients.phoneBulk.results.title")}
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutline color="success" />
                <Typography>
                  {t("clients.phoneBulk.results.created", {
                    count: bulkResult.created,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutline color="info" />
                <Typography>
                  {t("clients.phoneBulk.results.updated", {
                    count: bulkResult.updated,
                  })}
                </Typography>
              </Stack>
              {bulkResult.failed > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ErrorOutline color="error" />
                  <Typography color="error">
                    {t("clients.phoneBulk.results.failed", {
                      count: bulkResult.failed,
                    })}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {bulkResult.errors.length > 0 && (
              <>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowErrors((s) => !s)}
                >
                  {t("clients.phoneBulk.results.errors")} (
                  {bulkResult.errors.length})
                </Button>
                <Collapse in={showErrors}>
                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflow: "auto",
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    {bulkResult.errors.map((err, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        display="block"
                        color="error"
                      >
                        {t("clients.phoneBulk.results.errorRow", {
                          row: err.row,
                          message: err.message,
                        })}
                      </Typography>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && activeStep < 2 && (
          <Button onClick={handleBack} disabled={isUpdating}>
            {t("clients.phoneBulk.back")}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            {activeStep === 1
              ? isUpdating
                ? t("clients.phoneBulk.updating")
                : t("clients.phoneBulk.preview.updateBtn", {
                    count: validRows.length,
                  })
              : t("clients.phoneBulk.next")}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            {t("clients.phoneBulk.results.done")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
